import { useContext, useMemo } from 'react';

import { DrawingShape } from '../models';
import { SideAreaAndLabor } from '../utils';

import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { AreaCalculationType, MATERIAL_OPTIONS } from '~/core/constants';
import {
  calculateBaseShapeArea,
  getAreaAndLaborQuantity,
} from '~/core/utils/area.utils';
import { DrawingShapeContext } from '~/legacy/easy-estimator-page/context';

/**
 * @type UseShapeAreaReturn extends SideAreaAndLabor
 * @property {AreaCalculationType} selectedMaterialAreaType What type of calculation to use for the selected material area - raw or manufactured
 *  This is necessary for calls to calculateShapeArea
 *
 * @property {number} totalBaseCountertopArea This is the sum of the base areas for all the countertops, calculated for raw or manufactured values,
 *  depending on the selected material settings. Does not include backsplash or waterfall areas
 *
 * @property {number} totalMaterialArea the sum of the base areas for all the countertops, backsplash and waterfall areas
 */
type UseShapeAreaReturn = SideAreaAndLabor & {
  /**
   * What type of calculation to use for the selected material area - raw or manufactured
   * This is necessary for calls to calculateShapeArea
   */
  selectedMaterialAreaType: AreaCalculationType;
};

const EMPTY_ARRAY = [] satisfies DrawingShape[];

export const useShapeArea = (): UseShapeAreaReturn => {
  const selectedMaterial = useSelectedMaterial();
  const {
    getters: { drawingShapes },
  } = useContext(DrawingShapeContext);

  const selectedMaterialAreaType = useMemo(() => {
    const materialOption = MATERIAL_OPTIONS.find((option) =>
      selectedMaterial?.description?.match(option.nameMatcher)
    );

    return materialOption?.areaType ?? AreaCalculationType.Manufactured;
  }, [selectedMaterial?.description]);

  /**
   * this is the sum of the base areas for all the countertops, calculated for raw or manufactured values,
   * depending on the selected material settings
   */
  const totalBaseCountertopArea =
    drawingShapes?.reduce(
      (acc, next) =>
        acc + calculateBaseShapeArea(next, selectedMaterialAreaType),
      0
    ) ?? 0;

  const {
    totalBacksplashArea,
    totalWaterfallArea,
    totalWaterfallLaborEdgeLengths,
    totalEndCapLaborEdgeCount,
    totalEndSplashLaborEdgeCount,
  } = getAreaAndLaborQuantity(drawingShapes ?? EMPTY_ARRAY);

  const totalMaterialArea =
    totalBaseCountertopArea + totalBacksplashArea + totalWaterfallArea;

  return {
    selectedMaterialAreaType,
    totalBaseCountertopArea,
    totalMaterialArea,
    totalWaterfallArea,
    totalWaterfallLaborEdgeLengths,
    totalEndCapLaborEdgeCount,
    totalEndSplashLaborEdgeCount,
    totalBacksplashArea,
  };
};
